import { useEffect, useMemo } from "react"

import * as XLSX from "xlsx"

import { useDocumentPreviewSlowMs } from "@/features/chat/hooks/use-document-preview-slow-ms"
import { useFileContent } from "@/hooks/use-file-content"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { findTextWithFuzzyMatching, type FuzzyMatchResult } from "@/utils/fuzzy-text-matcher"
import { normalizeForMatching } from "@/utils/normalize-for-matching"

import styles from "../../file-viewer/styles/file-viewer.module.css"

type CellValue = string | number | boolean | Date

type WorkbookSheet = {
  name: string
  rows: CellValue[][]
}

type MatchLocation = {
  sheetIndex: number
  rowIndex: number
  columnIndex: number
}

type CitationHoverExcelMiniViewerProps = {
  documentUrl: string
  quoteSnippet: string
  chunkSnippet: string
  enabled: boolean
  /** When xlsx parse for the hover grid is slow, enables heavy preview gating. */
  onSlowRender?: () => void
}

type GridCell = {
  value: string
  isMatch: boolean
}

type ExcelPreviewGrid = {
  sheetName: string
  matchCellLabel: string | null
  rowStart: number
  columnStart: number
  rows: GridCell[][]
}

const MICRO_GRID_SIZE = 2
const MICRO_GRID_CONTEXT_DEPTH = MICRO_GRID_SIZE - 1
const MIN_CANDIDATE_LENGTH = 5
const MAX_CANDIDATE_LENGTH = 160
const MAX_CANDIDATE_COUNT = 24
const MIN_ACCEPTED_MATCH_SCORE = 1

function normalizeCell(cell: unknown): CellValue {
  if (
    cell instanceof Date ||
    typeof cell === "string" ||
    typeof cell === "number" ||
    typeof cell === "boolean"
  ) {
    return cell
  }
  if (cell !== null && cell !== undefined) {
    return String(cell)
  }
  return ""
}

function parseWorkbook(buffer: ArrayBuffer): WorkbookSheet[] | null {
  try {
    const workbook = XLSX.read(buffer, { type: "array" })
    if (!workbook.SheetNames.length) {
      return null
    }

    return workbook.SheetNames.map((sheetName) => {
      const worksheet = workbook.Sheets[sheetName]
      const ref = worksheet["!ref"]
      let rows: unknown[][] = []
      if (ref) {
        rows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          range: ref,
        }) as unknown[][]
      }
      if (!rows.length) {
        rows = [[]]
      }
      return {
        name: sheetName,
        rows: rows.map((row) => row.map(normalizeCell)),
      }
    })
  } catch {
    return null
  }
}

function toExcelColumnName(columnIndex: number): string {
  let index = columnIndex
  let letters = ""
  while (index >= 0) {
    letters = String.fromCharCode((index % 26) + 65) + letters
    index = Math.floor(index / 26) - 1
  }
  return letters
}

function formatMatchCellLabel(rowIndex: number, columnIndex: number): string {
  return `${toExcelColumnName(columnIndex)}${rowIndex + 1}`
}

function buildSearchCandidates(snippets: string[]): string[] {
  const candidates = new Set<string>()
  const splitRegex = /[\n\r.;:!?|]/g

  for (const snippet of snippets) {
    const normalizedSnippet = normalizeForMatching(snippet).trim()
    if (!normalizedSnippet) {
      continue
    }

    const fullCandidate =
      normalizedSnippet.length > MAX_CANDIDATE_LENGTH
        ? normalizedSnippet.slice(0, MAX_CANDIDATE_LENGTH).trim()
        : normalizedSnippet
    if (fullCandidate.length >= MIN_CANDIDATE_LENGTH) {
      candidates.add(fullCandidate)
    }

    const segments = normalizedSnippet.split(splitRegex).map((segment) => segment.trim())
    for (const segment of segments) {
      if (segment.length < MIN_CANDIDATE_LENGTH) {
        continue
      }
      const bounded =
        segment.length > MAX_CANDIDATE_LENGTH
          ? segment.slice(0, MAX_CANDIDATE_LENGTH).trim()
          : segment
      if (bounded.length >= MIN_CANDIDATE_LENGTH) {
        candidates.add(bounded)
      }
    }
  }

  const orderedCandidates = Array.from(candidates).sort((left, right) => right.length - left.length)
  if (orderedCandidates.length) {
    return orderedCandidates.slice(0, MAX_CANDIDATE_COUNT)
  }

  const fallback = snippets
    .map((snippet) => normalizeForMatching(snippet).trim())
    .find((snippet) => snippet.length > 0)
  return fallback ? [fallback] : []
}

function getMatchScore(match: FuzzyMatchResult, candidateLength: number): number {
  const qualityBoostByType: Record<FuzzyMatchResult["quality"], number> = {
    exact: 0.35,
    fuzzy: 0.25,
    partial: 0.1,
    weak: 0,
    none: 0,
  }
  const qualityBoost = qualityBoostByType[match.quality]
  const lengthBoost = Math.min(candidateLength, 80) / 400
  return match.confidence + qualityBoost + lengthBoost
}

function scoreCellAgainstCandidates(cellText: string, candidates: string[]): number {
  const normalizedCell = normalizeForMatching(cellText).trim()
  if (!normalizedCell) {
    return 0
  }

  let bestScore = 0
  for (const candidate of candidates) {
    const match = findTextWithFuzzyMatching(normalizedCell, candidate, {
      alreadyNormalized: true,
    })
    if (!match.found) {
      continue
    }

    const score = getMatchScore(match, candidate.length)
    if (score > bestScore) {
      bestScore = score
    }
  }
  return bestScore
}

function findFirstMatch(sheets: WorkbookSheet[], snippets: string[]): MatchLocation | null {
  const candidates = buildSearchCandidates(snippets)
  if (!candidates.length) {
    return null
  }

  let bestLocation: MatchLocation | null = null
  let bestScore = 0

  for (let sheetIndex = 0; sheetIndex < sheets.length; sheetIndex++) {
    const sheet = sheets[sheetIndex]
    for (let rowIndex = 0; rowIndex < sheet.rows.length; rowIndex++) {
      const row = sheet.rows[rowIndex]
      for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
        const cellText = String(row[columnIndex] ?? "")
        const matchScore = scoreCellAgainstCandidates(cellText, candidates)
        if (matchScore > bestScore && matchScore >= MIN_ACCEPTED_MATCH_SCORE) {
          bestScore = matchScore
          bestLocation = { sheetIndex, rowIndex, columnIndex }
        }
      }
    }
  }

  return bestLocation
}

function buildPreviewGrid(
  sheets: WorkbookSheet[],
  match: MatchLocation | null
): ExcelPreviewGrid | null {
  if (!sheets.length) {
    return null
  }

  const sheetIndex = match?.sheetIndex ?? 0
  const sheet = sheets[sheetIndex]

  const targetRow = match?.rowIndex ?? 0
  const targetColumn = match?.columnIndex ?? 0
  const rowStart = targetRow - MICRO_GRID_CONTEXT_DEPTH
  const columnStart = targetColumn - MICRO_GRID_CONTEXT_DEPTH

  const rows: GridCell[][] = []
  for (let rowOffset = 0; rowOffset < MICRO_GRID_SIZE; rowOffset++) {
    const rowIndex = rowStart + rowOffset
    const sourceRow = rowIndex >= 0 ? (sheet.rows[rowIndex] ?? []) : []
    const nextRow: GridCell[] = []
    for (let columnOffset = 0; columnOffset < MICRO_GRID_SIZE; columnOffset++) {
      const columnIndex = columnStart + columnOffset
      const value = columnIndex >= 0 ? String(sourceRow[columnIndex] ?? "") : ""
      nextRow.push({
        value,
        isMatch: !!match && rowIndex === targetRow && columnIndex === targetColumn,
      })
    }
    rows.push(nextRow)
  }

  return {
    sheetName: sheet.name,
    matchCellLabel: match ? formatMatchCellLabel(targetRow, targetColumn) : null,
    rowStart,
    columnStart,
    rows,
  }
}

export function CitationHoverExcelMiniViewer({
  documentUrl,
  quoteSnippet,
  chunkSnippet,
  enabled,
  onSlowRender,
}: CitationHoverExcelMiniViewerProps) {
  const slowThresholdMs = useDocumentPreviewSlowMs()
  const { data: buffer, isError, isLoading } = useFileContent(documentUrl, enabled)

  const snippets = useMemo(() => {
    return [quoteSnippet, chunkSnippet].filter(
      (snippet): snippet is string => snippet.trim().length > 0
    )
  }, [chunkSnippet, quoteSnippet])

  const { previewGrid, parseElapsedMs } = useMemo(() => {
    if (!enabled || !buffer) {
      return { previewGrid: null as ExcelPreviewGrid | null, parseElapsedMs: null as number | null }
    }
    const start = performance.now()
    const sheets = parseWorkbook(buffer)
    if (!sheets) {
      return { previewGrid: null, parseElapsedMs: performance.now() - start }
    }
    const match = findFirstMatch(sheets, snippets)
    const grid = buildPreviewGrid(sheets, match)
    return { previewGrid: grid, parseElapsedMs: performance.now() - start }
  }, [buffer, enabled, snippets])

  useEffect(() => {
    if (parseElapsedMs == null) {
      return
    }
    if (parseElapsedMs > slowThresholdMs) {
      logger.info("excel hover mini slow parse detected", { elapsed: Math.round(parseElapsedMs) })
      onSlowRender?.()
    }
  }, [onSlowRender, parseElapsedMs, slowThresholdMs])

  if (!enabled) {
    return null
  }

  if (isLoading) {
    return (
      <div className="bg-background/80 text-muted-foreground rounded-md border px-3 py-2 text-xs">
        Loading spreadsheet preview...
      </div>
    )
  }

  if (isError || !previewGrid) {
    return (
      <div className="bg-background/80 text-muted-foreground rounded-md border px-3 py-2 text-xs">
        Spreadsheet preview unavailable.
      </div>
    )
  }

  return (
    <div className="bg-background/80 rounded-md border p-2">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <span className="truncate font-medium">{previewGrid.sheetName}</span>
        {previewGrid.matchCellLabel && (
          <span className="text-muted-foreground shrink-0">Cell {previewGrid.matchCellLabel}</span>
        )}
      </div>
      <table className="w-full table-fixed border-collapse text-xs">
        <thead>
          <tr>
            <th className="bg-muted/40 border-border/50 w-8 border px-1 py-1 text-center font-medium">
              {" "}
            </th>
            {Array.from({ length: MICRO_GRID_SIZE }).map((_, columnOffset) => {
              const columnIndex = previewGrid.columnStart + columnOffset
              const label = columnIndex >= 0 ? toExcelColumnName(columnIndex) : ""
              return (
                <th
                  key={`column-header-${columnOffset}`}
                  className="bg-muted/40 border-border/50 border px-1 py-1 text-center font-medium"
                >
                  {label}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {previewGrid.rows.map((row, rowOffset) => {
            const rowIndex = previewGrid.rowStart + rowOffset
            const rowLabel = rowIndex >= 0 ? String(rowIndex + 1) : ""
            return (
              <tr key={rowIndex}>
                <th className="bg-muted/40 border-border/50 w-8 border px-1 py-1 text-right font-medium">
                  {rowLabel}
                </th>
                {row.map((cell, columnIndex) => (
                  <td
                    key={`${rowOffset}-${columnIndex}`}
                    title={cell.value}
                    className={cn(
                      styles.spreadSheetReadOnlyCell,
                      "border-border/50 max-w-[96px] truncate border px-1.5 py-1 align-top",
                      cell.isMatch && styles.citationHighlight
                    )}
                  >
                    {cell.value || " "}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
